/**
 * Перечисление для ролей пользователей в приложении.
 */
export const enum ROLES {
  Root = "root", // Наивысший уровень доступа со всеми разрешениями.
  Administrator = "administrator", // Средний уровень доступа с большинством разрешений.
  Operator = "operator", // Базовый уровень доступа с ограниченными разрешениями.
}

/**
 * Перечисление для различных областей доступа в приложении.
 */
export const enum SCOPES {
  AllowedToViewDashboard = "allowed-to-view-dashboard", // Доступ к просмотру панели инструментов.
  AllowedToViewChats = "allowed-to-view-view-chats", // Доступ к просмотру чатов.
  AllowedToViewClients = "allowed-to-view-clients", // Доступ к просмотру клиентов.
  AllowedToViewBotControl = "allowed-to-view-bot-control", // Доступ к управлению ботами.
  AllowedToViewAgenda = "allowed-to-view-agenda", // Доступ к просмотру календаря.
  AllowedToViewSettings = "allowed-to-view-settings", // Доступ к просмотру настроек.
  AllowedToViewChannelsSettings = "allowed-to-view-channels-settings", // Доступ к настройкам каналов.
  AllowedToViewTeamsSettings = "allowed-to-view-teams-settings", // Доступ к настройкам команд.
  AllowedToViewTemplatesSettings = "allowed-to-view-templates-settings", // Доступ к настройкам шаблонов.
  AllowedToViewClientCardSettings = "allowed-to-view-client-card-settings", // Доступ к настройкам карточек клиентов.
  AllowedToViewInventorySynchronization = "allowed-to-view-inventory-synchronization",
  AllowedToViewAllChats = "allowed-to-view-all-chats", // Доступ ко всем чатам.
  AllowedToAssignChatsToOtherTeams = "allowed-to-assign-chats-to-other-teams", // Доступ к назначению чатов другим командам.
  AllowedToEditPublicSnippets = "allowed-to-edit-public-snippets", // Доступ к редактированию публичных фрагментов.
  AllowedToDeletePublicSnippets = "allowed-to-delete-public-snippets", // Доступ к удалению публичных фрагментов.
  AllowedToEditSnippetsCategories = "allowed-to-edit-snippets-categories", // Доступ к редактированию категорий фрагментов.
  AllowedToCreateBot = "allowed-to-create-bot", // Доступ к созданию ботов.
  AllowedToToggleBot = "allowed-to-toggle-bot", // Доступ к включению и выключению ботов.
  AllowedToCreateChannels = "allowed-to-create-channels", // Доступ к созданию каналов.
  AllowedToCreateTemplatesForOtherTeams = "allowed-to-create-template-for-other-teams", // Доступ к созданию шаблонов для других команд.
  AllowedToCreateTeams = "allowed-to-create-teams", // Доступ к созданию команд.
  AllowedToCreateOperators = "allowed-to-create-operators", // Доступ к созданию операторов.
  AllowedToViewGeneralAnalytics = "allowed-to-view-general-analytics", // Доступ к просмотру общей аналитики.
  AllowedToViewChatsAnalytics = "allowed-to-view-chats-analytics", // Доступ к просмотру аналитики чатов.
  AllowedToViewOperatorsAnalytics = "allowed-to-view-operators-analytics", // Доступ к просмотру аналитики операторов.
  AllowedToFilterOperatorsAnalyticsByTeam = "allowed-to-filter-operators-analytics-by-team", // Доступ к фильтрации аналитики операторов по команде.
  AllowedToFilterOperatorsAnalyticsByOperator = "allowed-to-filter-operators-analytics-by-operator", // Доступ к фильтрации аналитики операторов по оператору.
  AllowedToDownloadContacts = "allowed-to-download-clients", // Доступ к загрузке контактов.
  AllowedToBotActions = "allowed-to-bot-actions", // Доступ к действиям ботов.
  AllowedToCreateTemplateAssignedToCategory = "allowed-to-create-template-assigned-to-category", // Доступ к созданию шаблонов, привязанных к категориям.
  AllowedToViewInvisibleChats = "allowed-to-view-invisible-chats", // Доступ к просмотру невидимых чатов.
  AllowedToViewCompletedChats = "allowed-to-view-completed-chats", // Доступ к просмотру завершенных чатов.
  AllowedToViewNonRespondedChats = "allowed-to-view-non-responded-chats",
  AllowedToViewNonAcceptedChats = "allowed-to-view-non-accepted-chats", // Доступ к просмотру не принятых чатов.
  AllowedToViewBillings = "allowed-to-view-billings", // Доступ к просмотру биллингов.
  AllowedToViewPaymentMethod = "allowed-to-view-credit-cards", // Доступ к просмотру методов оплаты.
  AllowedToViewOnboarding = "allowed-to-view-onboarding", // Доступ к просмотру процесса онбординга.
  AllowedToViewNotifications = "allowed-to-view-notifications", // Доступ к просмотру уведомлений.
  AllowedToViewNotificationsSettings = "allowed-to-view-notifications-settings", // Доступ к настройкам уведомлений.
  AllowedToStopAssistant = "allowed-to-stop-assistant", // Доступ к остановке бота.
  AllowedToViewInventory = "allowed-to-view-inventory", // Доступ к просмотру инвентаря.
  AllowedToDeleteEvent = "allowed-to-delete-event", // Доступ к удалению евента.
  AllowedToViewSettingsPermissions = "allowed-to-view-settings-permissions", // Доступ к настройкам разрешений.
  AllowedToEditAppointmentOperator = "allowed-to-edit-appointment-operator", // Доступ к редактированию оператора аппойнтмента.
}

/**
 * Сопоставление ролей с их разрешенными областями доступа.
 */
export const PERMISSIONS: {
  [key: string]: SCOPES[];
} = {
  [ROLES.Operator]: [
    SCOPES.AllowedToViewChats,
    SCOPES.AllowedToViewClients,
    SCOPES.AllowedToViewAgenda,
    SCOPES.AllowedToViewSettings,
    SCOPES.AllowedToViewTemplatesSettings,
    SCOPES.AllowedToViewDashboard,
    SCOPES.AllowedToViewOperatorsAnalytics,
    SCOPES.AllowedToViewNotifications,
    SCOPES.AllowedToViewNotificationsSettings,
    SCOPES.AllowedToViewInventory,
    SCOPES.AllowedToViewCompletedChats,
    SCOPES.AllowedToViewNonRespondedChats,
    SCOPES.AllowedToEditAppointmentOperator,
  ],
  [ROLES.Administrator]: [
    SCOPES.AllowedToViewDashboard,
    SCOPES.AllowedToViewChats,
    SCOPES.AllowedToViewClients,
    SCOPES.AllowedToViewAgenda,
    SCOPES.AllowedToViewSettings,
    SCOPES.AllowedToViewChannelsSettings,
    SCOPES.AllowedToViewTeamsSettings,
    SCOPES.AllowedToViewTemplatesSettings,
    SCOPES.AllowedToViewClientCardSettings,
    SCOPES.AllowedToViewInventorySynchronization,
    SCOPES.AllowedToViewAllChats,
    SCOPES.AllowedToAssignChatsToOtherTeams,
    SCOPES.AllowedToEditPublicSnippets,
    SCOPES.AllowedToDeletePublicSnippets,
    SCOPES.AllowedToEditSnippetsCategories,
    SCOPES.AllowedToCreateTemplatesForOtherTeams,
    SCOPES.AllowedToCreateOperators,
    SCOPES.AllowedToViewGeneralAnalytics,
    SCOPES.AllowedToViewChatsAnalytics,
    SCOPES.AllowedToViewOperatorsAnalytics,
    SCOPES.AllowedToFilterOperatorsAnalyticsByTeam,
    SCOPES.AllowedToFilterOperatorsAnalyticsByOperator,
    SCOPES.AllowedToCreateTemplateAssignedToCategory,
    SCOPES.AllowedToViewCompletedChats,
    SCOPES.AllowedToViewInvisibleChats,
    SCOPES.AllowedToViewBillings,
    SCOPES.AllowedToViewPaymentMethod,
    SCOPES.AllowedToViewNotifications,
    SCOPES.AllowedToViewNotificationsSettings,
    SCOPES.AllowedToViewInventory,
    SCOPES.AllowedToViewNonAcceptedChats,
    SCOPES.AllowedToStopAssistant,
    SCOPES.AllowedToViewNonRespondedChats,
    SCOPES.AllowedToDeleteEvent,
    SCOPES.AllowedToViewSettingsPermissions,
    SCOPES.AllowedToEditAppointmentOperator,
  ],
  [ROLES.Root]: [
    SCOPES.AllowedToViewDashboard,
    SCOPES.AllowedToViewChats,
    SCOPES.AllowedToViewClients,
    SCOPES.AllowedToViewAgenda,
    SCOPES.AllowedToViewSettings,
    SCOPES.AllowedToViewChannelsSettings,
    SCOPES.AllowedToViewTeamsSettings,
    SCOPES.AllowedToViewTemplatesSettings,
    SCOPES.AllowedToViewClientCardSettings,
    SCOPES.AllowedToViewInventorySynchronization,
    SCOPES.AllowedToViewAllChats,
    SCOPES.AllowedToAssignChatsToOtherTeams,
    SCOPES.AllowedToEditPublicSnippets,
    SCOPES.AllowedToDeletePublicSnippets,
    SCOPES.AllowedToEditSnippetsCategories,
    SCOPES.AllowedToCreateChannels,
    SCOPES.AllowedToCreateTemplatesForOtherTeams,
    SCOPES.AllowedToCreateTeams,
    SCOPES.AllowedToCreateOperators,
    SCOPES.AllowedToViewGeneralAnalytics,
    SCOPES.AllowedToViewChatsAnalytics,
    SCOPES.AllowedToViewOperatorsAnalytics,
    SCOPES.AllowedToFilterOperatorsAnalyticsByTeam,
    SCOPES.AllowedToFilterOperatorsAnalyticsByOperator,
    SCOPES.AllowedToDownloadContacts,
    SCOPES.AllowedToBotActions,
    SCOPES.AllowedToCreateTemplateAssignedToCategory,
    SCOPES.AllowedToViewCompletedChats,
    SCOPES.AllowedToViewInvisibleChats,
    SCOPES.AllowedToViewNonAcceptedChats,
    SCOPES.AllowedToViewBillings,
    SCOPES.AllowedToViewPaymentMethod,
    SCOPES.AllowedToViewOnboarding,
    SCOPES.AllowedToViewNotifications,
    SCOPES.AllowedToViewNotificationsSettings,
    SCOPES.AllowedToViewInventory,
    SCOPES.AllowedToStopAssistant,
    SCOPES.AllowedToViewNonRespondedChats,
    SCOPES.AllowedToDeleteEvent,
    SCOPES.AllowedToViewSettingsPermissions,
  ],
  unknown: [], // Для неизвестных ролей доступ к областям не предоставляется.
};
